import { useState, useCallback } from 'react'
import { toast } from '../services/toastService'
import { entityAPI } from '../services/api'
import { 
  trackFavoriteToggle, 
  trackEntityShare,
  trackEvent,
  trackActivation
} from '../utils/analytics'
import { createLogger } from '../utils/logger'

const logger = createLogger('useEntityPanelActions')

/**
 * Custom hook for entity panel actions (favorite, share, join channel, confirm location)
 * Extracts handler logic from EntityDisplayPanel
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.user - Current user object
 * @param {Object} params.entityDetails - Entity details object
 * @param {Function} params.isFavorite - Function to check if entity is favorited
 * @param {Function} params.toggleFavorite - Function to toggle favorite status
 * @param {boolean} params.isTogglingFavorite - Loading state for favorite toggle
 * @param {Function} params.onTabChange - Tab change handler
 * @param {Function} params.onConfirmLocationClick - Handler to trigger marker popup
 * @param {boolean} params.isOwnSighting - Whether user owns the latest sighting
 * @param {Function} params.t - Translation function
 * 
 * @returns {Object} Handler functions and state
 * 
 * @example
 * const { handleConfirmLocationClick, isJoiningChannel } = useEntityPanelActions({
 *   user, entityDetails, isFavorite, toggleFavorite, 
 *   isTogglingFavorite, onTabChange, onConfirmLocationClick, 
 *   isOwnSighting, t
 * })
 */
export function useEntityPanelActions({
  user,
  entityDetails,
  isFavorite,
  toggleFavorite,
  isTogglingFavorite,
  onTabChange,
  onConfirmLocationClick,
  isOwnSighting,
  t
}) {
  const [isJoiningChannel, setIsJoiningChannel] = useState(false)

  // Handle favorite button click
  const handleFavoriteClick = useCallback(async () => {
    if (isTogglingFavorite) return
    
    if (!user) {
      toast.info(
        t('favorites.signUpToSave') || 'Sign up to save this to your favorites and join the channel!',
        { autoClose: 6000 }
      )
      
      trackEvent('Conversion', 'signup_prompt', 'favorite_button_clicked', null, {
        entity_id: entityDetails.id,
        entity_name: entityDetails.name,
        source: 'entity_detail_favorite_button'
      })
      
      if (onTabChange) {
        onTabChange('auth')
      }
      return
    }
    
    if (toggleFavorite) {
      const wasFavorite = isFavorite && isFavorite(entityDetails.id)
      const result = await toggleFavorite(entityDetails.id)
      
      trackFavoriteToggle(
        entityDetails.id, 
        entityDetails.name, 
        wasFavorite ? 'remove' : 'add',
        'immersive_panel'
      )
      
      if (!wasFavorite) {
        trackActivation('first_favorite', { entity_id: entityDetails.id })
      }
      
      if (!wasFavorite && result?.data?.channelId && onTabChange) {
        const channelId = result.data.channelId
        
        // Backend invalidates cache, so just wait briefly for state updates
        setTimeout(() => {
          onTabChange('contact', null, channelId)
          toast.success(t('favorites.channelJoined') || 'You\'ve joined the channel! Check the Contact tab to connect with the community.')
        }, 500)
      }
    }
  }, [isTogglingFavorite, user, t, entityDetails, isFavorite, toggleFavorite, onTabChange])

  // Handle join discussion button
  const handleJoinDiscussion = useCallback(async () => {
    if (isJoiningChannel) return
    
    if (!user) {
      toast.info(
        t('channel.signUpToJoin') || 'Sign up to join the discussion',
        { autoClose: 4000 }
      )
      
      trackEvent('Conversion', 'signup_prompt', 'join_discussion_clicked', null, {
        entity_id: entityDetails.id,
        entity_name: entityDetails.name,
        source: 'entity_detail_join_discussion_button'
      })
      
      if (onTabChange) {
        onTabChange('auth')
      }
      return
    }
    
    setIsJoiningChannel(true)
    
    try {
      const response = await entityAPI.getEntityChannel(entityDetails.id)
      const channelId = response.data.channelId
      
      trackEvent('Channel', 'join_discussion', 'entity_detail_button', null, {
        entity_id: entityDetails.id,
        entity_name: entityDetails.name,
        channel_id: channelId
      })
      
      // Backend invalidates cache, so just wait briefly for state updates
      setTimeout(() => {
        if (onTabChange) {
          onTabChange('contact', null, channelId)
        }
        toast.success(t('channel.joined'))
      }, 500)
    } catch (error) {
      logger.error('Failed to join channel', error)
      toast.error(t('channel.joinError') || 'Failed to join channel')
    } finally {
      setIsJoiningChannel(false)
    }
  }, [isJoiningChannel, user, t, entityDetails, onTabChange])

  // Handle share button click
  const handleShare = useCallback(() => {    const url = `${window.location.origin}/entity/${entityDetails.id}`
    
    if (navigator.share) {
      navigator.share({
        title: entityDetails.name,
        text: entityDetails.description || `Check out ${entityDetails.name} on TrckIt`,
        url: url
      }).then(() => {
        trackEntityShare(entityDetails.id, entityDetails.name, 'native', 'immersive_panel')
      }).catch((error) => {
        if (error.name !== 'AbortError') {
          logger.error('Share failed', error)
        }
      })
    } else {
      navigator.clipboard.writeText(url).then(() => {
        toast.success(t('common.linkCopied') || 'Link copied to clipboard!')
        trackEntityShare(entityDetails.id, entityDetails.name, 'clipboard', 'immersive_panel')
      }).catch((error) => {
        logger.error('Copy to clipboard failed', error)
        toast.error(t('common.copyFailed') || 'Failed to copy link')
      })
    }
  }, [entityDetails, t])

  // Handle confirm location button click
  const handleConfirmLocationClick = useCallback(() => {
    if (!user) {
      toast.info(t('toast.info.loginToConfirm'))
      if (onTabChange) onTabChange('auth')
      return
    }
    
    if (isOwnSighting) {
      toast.info(t('toast.info.cannotConfirmOwn', "You can't confirm your own sighting"))
      return
    }
    
    // Trigger opening the latest sighting marker in confirmation mode
    if (onConfirmLocationClick) {
      onConfirmLocationClick()
    }
  }, [user, onConfirmLocationClick, onTabChange, isOwnSighting, t])

  return {
    isJoiningChannel,
    handleFavoriteClick,
    handleJoinDiscussion,
    handleShare,
    handleConfirmLocationClick
  }
}